import { useEffect, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import { useStores } from "../../../lib/query/queries/useStoreQueries";
import { toast } from "react-toastify";
import { useAuthCheck } from "../../../utilities/authCheck";
import { useNavigate } from "react-router-dom";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import {
  Shop,
  Clipboard,
  ArrowClockwise,
  PlusLg,
  ExclamationTriangle,
  ShieldExclamation,
} from "react-bootstrap-icons";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("ss-styles")) {
  const s = document.createElement("style");
  s.id = "ss-styles";
  s.textContent = `
    @keyframes _ss_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._ss_sk { animation: _ss_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .ss-card { transition: transform 0.22s cubic-bezier(0.34,1.24,0.64,1), box-shadow 0.22s ease; cursor: pointer; }
    .ss-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
    .ss-card:active { transform: scale(.985); }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const CATEGORY_COLORS = {
  "fast-food": "#007AFF",
  liquor: "#34C759",
  grocery: "#FF9500",
};

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_ss_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const CategoryPill = ({ category }) => {
  const color = CATEGORY_COLORS[category] ?? "#8E8E93";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        backgroundColor: `${color}1A`,
        color,
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "capitalize",
        letterSpacing: "0.01em",
      }}
    >
      {category}
    </span>
  );
};

const StoreCard = ({ store, onNavigate, onCopy }) => (
  <div
    className="ss-card"
    onClick={() => onNavigate(store.id)}
    style={{
      backgroundColor: "#fff",
      borderRadius: 16,
      border: "1px solid rgba(60,60,67,0.09)",
      padding: "16px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "rgba(0,122,255,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Shop size={17} color="#007AFF" />
      </div>
      <CategoryPill category={store?.store_category} />
    </div>

    <h3
      style={{
        fontSize: "0.9375rem",
        fontWeight: 600,
        color: "#1c1c1e",
        margin: "0 0 6px",
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {store?.store_name}
    </h3>

    <div
      style={{
        marginTop: "auto",
        paddingTop: 10,
        borderTop: "1px solid rgba(60,60,67,0.07)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span
        style={{
          fontSize: "0.72rem",
          color: "rgba(60,60,67,0.42)",
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        #{store?.id}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onCopy(store?.id);
        }}
        title="Copy Store ID"
        style={{
          background: "none",
          border: "none",
          padding: 4,
          color: "rgba(0,122,255,0.75)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Clipboard size={13} />
      </button>
    </div>
  </div>
);

const SkeletonGrid = () => (
  <Row className="g-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Col xs={12} sm={6} lg={4} key={i}>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 16,
            border: "1px solid rgba(60,60,67,0.08)",
            padding: 16,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <Skel w={40} h={40} r={10} />
            <Skel w={68} h={22} r={11} />
          </div>
          <Skel w="78%" h={18} r={6} style={{ marginBottom: 16 }} />
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10 }}>
            <Skel w={50} h={12} r={5} />
            <Skel w={16} h={16} r={4} />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

const EmptyState = () => (
  <div
    style={{
      textAlign: "center",
      padding: "64px 24px",
      background: "#fff",
      border: "1px solid rgba(60,60,67,0.08)",
      borderRadius: 16,
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: 16,
        background: "rgba(60,60,67,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px",
      }}
    >
      <Shop size={26} color="rgba(60,60,67,0.28)" />
    </div>
    <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>No Stores Found</h4>
    <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.45)", margin: 0 }}>
      There are no stores available at the moment.
    </p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div
    style={{
      textAlign: "center",
      padding: "48px 24px",
      background: "#fff",
      border: "1px solid rgba(60,60,67,0.08)",
      borderRadius: 16,
    }}
  >
    <div
      style={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "rgba(255,59,48,.10)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px",
      }}
    >
      <ExclamationTriangle size={26} color="#FF3B30" />
    </div>
    <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>Error Loading Stores</h4>
    <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 20 }}>{error}</p>
    <button
      onClick={onRetry}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "10px 22px",
        background: "#007AFF",
        color: "#fff",
        border: "none",
        borderRadius: 12,
        fontSize: "0.9rem",
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: FONT,
      }}
    >
      <ArrowClockwise size={16} /> Try Again
    </button>
  </div>
);

const StoresSummary = () => {
  const { isAuthorized, isLoading: isAuthLoading } = useAuthCheck();
  const navigate = useNavigate();

  const {
    data: stores = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStores({
    enabled: isAuthorized,
    // Preserves the original "Invalid data format received from server"
    // guard against a malformed (non-array) response.
    select: (response) => {
      if (!response || !Array.isArray(response)) {
        throw new Error("Invalid data format received from server");
      }
      return response;
    },
  });

  const errorMessage = error?.message || "Failed to fetch stores. Please try again later.";

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Store ID copied to clipboard!"))
      .catch(() => toast.error("Failed to copy Store ID"));
  }, []);

  useEffect(() => {
    if (isError) toast.error("Failed to load stores. Please refresh the page.");
  }, [isError]);

  if (isAuthLoading || !isAuthorized) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          {!isAuthLoading && !isAuthorized && (
            <>
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 18,
                  backgroundColor: "rgba(255,149,0,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                <ShieldExclamation size={28} color="#FF9500" />
              </div>
              <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>
                Redirecting…
              </h3>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <PullToRefreshWrapper onRefresh={refetch}>
      <Seo {...generateNoIndexSEO("Stores Summary")} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px 48px", fontFamily: FONT }}>

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: "0 0 4px" }}>
              Stores
            </h2>
            <p style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.48)", margin: 0 }}>
              Manage your store listings and categories
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => navigate("/sup/stores/add")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 16px",
                borderRadius: 20,
                border: "none",
                backgroundColor: "#007AFF",
                color: "#fff",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: FONT,
              }}
            >
              <PlusLg size={14} /> Add Store
            </button>
            {/* <button
              onClick={fetchStoresData}
              disabled={isLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 16px",
                borderRadius: 20,
                border: "1px solid rgba(0,122,255,0.35)",
                backgroundColor: "transparent",
                color: "#007AFF",
                fontSize: "0.8125rem",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.5 : 1,
                fontFamily: FONT,
                minWidth: 96,
                justifyContent: "center",
              }}
            >
              <ArrowClockwise
                size={14}
                style={isLoading ? { animation: "_ss_sk 1s linear infinite" } : {}}
              />
              Refresh
            </button> */}
          </div>
        </div>

        {/* Error banner (non-blocking, dismissible) */}
        {isError && !isLoading && stores.length > 0 && (
          <div
            style={{
              backgroundColor: "rgba(255,59,48,0.08)",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: "0.875rem", color: "#FF3B30" }}>{errorMessage}</span>
            <button
              onClick={refetch}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "0.8125rem",
                color: "#007AFF",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <SkeletonGrid />
        ) : isError && stores.length === 0 ? (
          <ErrorState error={errorMessage} onRetry={refetch} />
        ) : stores.length === 0 ? (
          <EmptyState />
        ) : (
          <Row className="g-3">
            {stores.map((store) => (
              <Col xs={12} sm={6} lg={4} key={store?.id}>
                <StoreCard
                  store={store}
                  onNavigate={(id) => navigate(`/sup/stores/${id}`)}
                  onCopy={copyToClipboard}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </PullToRefreshWrapper>
  );
};

export default StoresSummary;
